import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { GiteaOrgPickerExtension } from './components/scaffolder/GiteaOrgPicker';
import { HumanitecProjectPickerExtension } from './components/scaffolder/HumanitecProjectPicker';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage, SettingsLayout, UserSettingsGeneral, UserSettingsAuthProviders, UserSettingsFeatureFlags } from '@backstage/plugin-user-settings';
import { AiAgentChatPage } from '@internal/plugin-ai-agent-chat';
import { OnboardingWizardPage } from '@internal/plugin-onboarding-wizard';
import { OnboardingGate } from './components/onboarding/OnboardingGate';
import { RefreshIntervalCard } from './components/settings/RefreshIntervalCard';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
} from '@backstage/core-components';
import { KyndrylSignInPage } from './components/signin/KyndrylSignInPage';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { KyndrylCatalogNode } from './components/catalog/KyndrylCatalogNode';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { HomePage } from './components/home/HomePage';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { UnifiedThemeProvider } from '@backstage/theme';

const isWipVisible = () => {
  try {
    const flags = JSON.parse(localStorage.getItem('featureFlags') || '{}');
    return flags['show-wip-templates'] === 1;
  } catch {
    return false;
  }
};

const wipTemplateFilter = (entity: { metadata?: { tags?: string[] } }) => {
  if (isWipVisible()) return true;
  return !(entity?.metadata?.tags ?? []).includes('wip');
};


import { kyndrylDarkTheme, kyndrylLightTheme } from './theme/kyndrylTheme';

const app = createApp({
  apis,
  featureFlags: [{ name: 'show-wip-templates', pluginId: '' }],
  themes: [{
    id: 'kyndryl-dark',
    title: 'Kyndryl Dark',
    variant: 'dark',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={kyndrylDarkTheme} children={children} />
    ),
  }, {
    id: 'kyndryl-light',
    title: 'Kyndryl Light',
    variant: 'light',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={kyndrylLightTheme} children={children} />
    ),
  }],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <KyndrylSignInPage {...props} />,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          templateFilter={wipTemplateFilter}
          groups={[
            {
              title: 'Demo Templates',
              filter: entity => entity?.spec?.type === 'demo',
            },
            {
              title: 'Test Templates',
              filter: entity => entity?.spec?.type === 'test',
            },
            {
              title: 'Resources',
              filter: entity => entity?.spec?.type === 'resource',
            },
          ]}
        >
          <ScaffolderFieldExtensions>
            <GiteaOrgPickerExtension />
            <HumanitecProjectPickerExtension />
          </ScaffolderFieldExtensions>
        </ScaffolderPage>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout>
        <SettingsLayout.Route path="/general" title="General">
          <>
            <UserSettingsGeneral />
            <RefreshIntervalCard />
          </>
        </SettingsLayout.Route>
        <SettingsLayout.Route path="/auth-providers" title="Auth Providers">
          <UserSettingsAuthProviders />
        </SettingsLayout.Route>
        <SettingsLayout.Route path="/feature-flags" title="Feature Flags">
          <UserSettingsFeatureFlags />
        </SettingsLayout.Route>
      </SettingsLayout>
    </Route>
    <Route path="/ai-agent" element={<AiAgentChatPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage renderNode={KyndrylCatalogNode} />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/devtools" element={<DevToolsPage />} />
    <Route path="/catalog-unprocessed" element={<CatalogUnprocessedEntitiesPage />} />
    {/* Required for the plugin routing system; the wizard is rendered via OnboardingGate, not this route */}
    <Route path="/onboarding" element={<OnboardingWizardPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <OnboardingGate>
        <Root>{routes}</Root>
      </OnboardingGate>
    </AppRouter>
  </>,
);
